/**
 * OracleBell — persistent notification bell in the admin header.
 *
 * Answers one question in <5 seconds: "What requires my attention right now?"
 *
 * Data comes from the shared OracleContext (single 15s poll, tab-visibility
 * aware). The bell itself is presentational — no polling, no local timers.
 *
 * Behaviour:
 *   • Panel closes on outside-click OR on route navigation.
 *   • Events with a valid `href` are clickable rows; events without an href
 *     render as read-only lines (no false affordance).
 *   • "Mark all read" advances the per-admin server cursor.
 *   • Presence strip collapses to a chip on mobile — expandable on tap.
 *   • aria-live announces new unread events to screen readers.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, X, CheckCheck, AlertTriangle, AlertCircle, Info, Users, Loader2, Circle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { oracleEvents, oracleMarkRead } from "../../lib/api";
import { useOracle } from "../../context/OracleContext";

const SEV_META = {
  critical: { color: "#D92D20", icon: AlertCircle,    label: "Critical" },
  warning:  { color: "#E0A526", icon: AlertTriangle,  label: "Warning"  },
  info:     { color: "#3B82F6", icon: Info,           label: "Info"     },
  team:     { color: "#3FB950", icon: Users,          label: "Team"     },
};

const relTime = (iso) => {
  if (!iso) return "";
  try {
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  } catch { return iso; }
};

const relTimeShort = (ms) => {
  if (!ms) return "…";
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

export const OracleBell = ({ opToken, userToken }) => {
  const { summary, presence: rosterCtx, updatedAt, refreshSummary } = useOracle();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [freshTick, setFreshTick] = useState(0);
  const [presenceExpanded, setPresenceExpanded] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const nav = useNavigate();
  const loc = useLocation();

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await oracleEvents(opToken || null, userToken || null,
                                    { limit: 50, resolved: false });
      setEvents(r.items || []);
    } catch (e) {
      toast.error("Failed to load Oracle events");
    } finally { setLoading(false); }
  }, [opToken, userToken]);

  // Load events when panel opens
  useEffect(() => { if (open) loadEvents(); }, [open, loadEvents]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Freshness label ticker — 1Hz while panel open
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setFreshTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  const onMarkAllRead = useCallback(async () => {
    setMarkingRead(true);
    try {
      await oracleMarkRead(opToken || null, userToken || null);
      await refreshSummary();
      toast.success("All Oracle notifications marked read");
    } catch (e) {
      toast.error("Mark-read failed");
    } finally { setMarkingRead(false); }
  }, [opToken, userToken, refreshSummary]);

  const onSelectEvent = (ev) => {
    if (!ev.href) return;  // non-deep-linked events are read-only
    setOpen(false);
    nav(ev.href);
  };

  const total = summary?.total_unread || 0;
  const sev = summary?.by_severity || {};

  // Group events by severity for panel display
  const grouped = useMemo(() => {
    const g = { critical: [], warning: [], info: [], team: [] };
    for (const e of events) {
      if (g[e.severity]) g[e.severity].push(e);
    }
    return g;
  }, [events]);

  // Highest severity present drives the bell dot color
  const dotColor =
    (sev.critical || 0) > 0 ? SEV_META.critical.color :
    (sev.warning  || 0) > 0 ? SEV_META.warning.color :
    (sev.info     || 0) > 0 ? SEV_META.info.color :
    (sev.team     || 0) > 0 ? SEV_META.team.color :
    null;

  // Screen-reader announcement (renders off-screen)
  const srAnnouncement = total > 0
    ? `${total} unread Oracle notification${total > 1 ? "s" : ""}`
    : "";

  return (
    <div className="relative">
      {/* Off-screen SR-only region for polite announcements */}
      <span className="sr-only" aria-live="polite" role="status">
        {srAnnouncement}
      </span>

      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={total > 0 ? `${total} unread Oracle notifications` : "Oracle notifications"}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="oracle-bell"
        className="relative flex items-center justify-center w-9 h-9 border border-[#2D2A26] text-[#A39E93] hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
      >
        <Bell size={15} />
        {total > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center border border-[#0a0907] font-mono text-[9px] font-bold"
            style={{ background: dotColor, color: "#0a0907" }}
            data-testid="oracle-bell-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="oracle-panel"
          role="dialog"
          aria-label="Oracle notifications"
          className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-11 z-50 w-[min(420px,calc(100vw-16px))] max-h-[70vh] flex flex-col border border-[#D92D20] bg-[#0a0907] shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-[#2D2A26] px-3 py-2">
            <div className="flex items-center gap-2 text-[#D92D20]">
              <Bell size={12} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Oracle Feed</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onMarkAllRead}
                disabled={markingRead || total === 0}
                data-testid="oracle-mark-read"
                className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#A39E93] hover:text-[#3FB950] disabled:opacity-40 flex items-center gap-1"
              >
                <CheckCheck size={11} /> {markingRead ? "…" : "Mark all read"}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-[#A39E93] hover:text-[#D92D20]"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 py-1">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-[#757065] font-mono text-[11px]">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </div>
            ) : events.length === 0 ? (
              <div className="px-3 py-6 text-center text-[11px] font-mono text-[#757065]">
                All quiet. Nothing requires your attention.
              </div>
            ) : (
              ["critical", "warning", "info", "team"].map((k) => {
                const rows = grouped[k];
                if (!rows.length) return null;
                const meta = SEV_META[k];
                const Icon = meta.icon;
                return (
                  <div key={k} data-testid={`oracle-group-${k}`}>
                    <div className="px-3 py-1.5 flex items-center gap-2 text-[9px] font-mono uppercase tracking-[0.25em]" style={{ color: meta.color }}>
                      <Icon size={10} /> {meta.label} · {rows.length}
                    </div>
                    <ul>
                      {rows.map((ev) => {
                        const clickable = !!ev.href;
                        const RowTag = clickable ? "button" : "div";
                        return (
                          <li key={ev.id}>
                            <RowTag
                              {...(clickable
                                ? { type: "button", onClick: () => onSelectEvent(ev) }
                                : { "aria-disabled": "true" })}
                              data-testid={`oracle-event-${ev.id}`}
                              className={`w-full text-left px-3 py-2 border-l-2 flex flex-col gap-0.5 ${
                                clickable ? "hover:bg-[#131211] cursor-pointer" : "opacity-80 cursor-default"
                              }`}
                              style={{ borderLeftColor: meta.color }}
                            >
                              <div className="text-[12px] text-[#E4DFD3] font-medium leading-snug">
                                {ev.title}
                              </div>
                              {ev.message && (
                                <div className="text-[11px] text-[#A39E93] truncate">{ev.message}</div>
                              )}
                              <div className="flex items-center justify-between mt-0.5">
                                <span className="text-[9px] font-mono text-[#757065] uppercase tracking-[0.15em]">
                                  {ev.category}
                                </span>
                                <span className="text-[9px] font-mono text-[#757065]">
                                  {relTime(ev.ts)}
                                </span>
                              </div>
                            </RowTag>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-[#2D2A26] px-3 py-2 flex items-center justify-between">
            <span
              className="text-[9px] font-mono text-[#757065] uppercase tracking-[0.2em]"
              data-testid="oracle-freshness"
              aria-live="off"
            >
              {/* freshTick is read to trigger re-render every 1s */}
              {freshTick >= 0 ? `Updated ${relTimeShort(updatedAt)}` : ""}
            </span>
            <Link
              to="/admin/security/oracle"
              onClick={() => setOpen(false)}
              className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#A39E93] hover:text-[#D92D20]"
              data-testid="oracle-view-full"
            >
              History →
            </Link>
          </div>

          {/* Presence strip — collapses on mobile to a single chip */}
          {rosterCtx?.roster?.length > 0 && (
            <div className="border-t border-[#2D2A26] px-3 py-2 bg-[#0d0c0a]" data-testid="oracle-presence">
              <button
                type="button"
                onClick={() => setPresenceExpanded((v) => !v)}
                className="w-full flex items-center justify-between text-left"
                data-testid="oracle-presence-toggle"
                aria-expanded={presenceExpanded}
              >
                <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#3FB950]">
                  Team Presence
                </span>
                <span className="flex items-center gap-2 text-[9px] font-mono text-[#757065]">
                  {rosterCtx.counts.online} online · {rosterCtx.counts.idle} idle
                  <ChevronDown
                    size={10}
                    className={`transition-transform ${presenceExpanded ? "rotate-180" : ""} hidden sm:block`}
                  />
                  <ChevronDown
                    size={10}
                    className={`transition-transform ${presenceExpanded ? "rotate-180" : ""} sm:hidden`}
                  />
                </span>
              </button>
              {/* Desktop: always show roster. Mobile: only when expanded. */}
              <ul
                className={`space-y-1 max-h-[140px] overflow-y-auto mt-1.5 ${presenceExpanded ? "block" : "hidden sm:block"}`}
              >
                {rosterCtx.roster.slice(0, 8).map((p, i) => {
                  const color = p.state === "online" ? "#3FB950" : p.state === "idle" ? "#E0A526" : "#757065";
                  return (
                    <li
                      key={`${p.actor_email}-${i}`}
                      data-testid={`oracle-presence-row-${p.state}`}
                      className="flex items-center gap-2 text-[11px] font-mono"
                    >
                      <Circle size={7} fill={color} className="shrink-0" style={{ color }} />
                      <span className="text-[#E4DFD3] truncate">{p.handle}</span>
                      <span className="text-[#757065] text-[9px] uppercase tracking-[0.2em]">{p.role}</span>
                      <span className="ml-auto text-[#757065] text-[9px]">
                        {p.state === "online" ? "now" : p.state === "idle" ? "idle" : relTime(p.last_seen_at)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OracleBell;
